const ADMIN_API =
  import.meta.env.VITE_ADMIN_API_URL || "http://localhost:3001/api";

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("merryflix_admin_token");
}

async function request<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const isFormData = options?.body instanceof FormData;

  const res = await fetch(`${ADMIN_API}${path}`, {
    ...options,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      // Only set JSON content-type when caller did NOT pass a FormData body.
      // For multipart uploads, the browser sets the boundary itself.
      ...(isFormData
        ? options?.headers
        : { "Content-Type": "application/json", ...(options?.headers ?? {}) }),
    },
  });

  if (!res.ok) {
    // Try to surface a structured error from the backend so callers can
    // branch on `code` (FILE_TOO_LARGE, UNSUPPORTED_MEDIA_TYPE, etc.).
    let payload: any = null;
    try {
      payload = await res.json();
    } catch {
      try {
        payload = { error: await res.text() };
      } catch {
        payload = { error: "Request failed" };
      }
    }
    const err: any = new Error(payload?.error || "Request failed");
    err.code = payload?.code;
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export interface ImageUploadResponse {
  success: true;
  url: string;
  key: string;
  sizeBytes: number;
  mimeType: string;
  width: number;
  height: number;
}

export interface AudioUploadResponse {
  success: true;
  url: string;
  key: string;
  sizeBytes: number;
  mimeType: string;
  durationSec: number | null;
}

export const adminApi = {
  get: <T = any>(path: string) => request<T>(path),
  post: <T = any>(path: string, data: any) =>
    request<T>(path, { method: "POST", body: JSON.stringify(data) }),
  put: <T = any>(path: string, data: any) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data) }),
  patch: <T = any>(path: string, data: any) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data) }),
  delete: <T = any>(path: string) => request<T>(path, { method: "DELETE" }),

  /** Multipart upload — caller picks the path. Field name is "file". */
  uploadFile: <T = any>(path: string, file: File): Promise<T> => {
    const fd = new FormData();
    fd.append("file", file);
    return request<T>(path, { method: "POST", body: fd });
  },

  uploadImage: (file: File) =>
    adminApi.uploadFile<ImageUploadResponse>("/admin/uploads/image", file),

  uploadAudio: (file: File) =>
    adminApi.uploadFile<AudioUploadResponse>("/admin/uploads/audio", file),
};

export interface MediaUploadResponse {
  success: true;
  file: {
    name: string;
    relativePath: string; // e.g. "bg/dinner-2026-07-20.jpg"
    url: string;          // served at "/static-defaults/{relativePath}"
    kind: 'bg' | 'fpv_flower' | 'fpv_ring' | 'fpv_dinner';
    sizeBytes: number;
    mimeType: string;
    createdAt: string;
  };
}

export interface StaticDefaultsManifest {
  success: boolean;
  baseUrl: string;
  kinds: Record<'bg' | 'fpv_flower' | 'fpv_ring' | 'fpv_dinner', string[]>;
}

export const adminMediaApi = {
  /** Returns the current seeded-image manifest grouped by kind. */
  manifest: () =>
    request<StaticDefaultsManifest>('/static-defaults-manifest'),

  /** Upload an image to `public/{kind}/` on disk. */
  upload: (kind: 'bg' | 'fpv_flower' | 'fpv_ring' | 'fpv_dinner', file: File) =>
    adminApi.uploadFile<MediaUploadResponse>(`/admin/media/${kind}`, file),

  /** Remove a single seeded image. */
  delete: (kind: 'bg' | 'fpv_flower' | 'fpv_ring' | 'fpv_dinner', filename: string) =>
    adminApi.delete<{ success: true }>(`/admin/media/${kind}/${encodeURIComponent(filename)}`),
};